/**
 * Widget compacto "última sync ARCA" para el Calendario Fiscal.
 * ORR-100 Fase 1 — discoverability (CRITICAL #31).
 *
 * Muestra el tiempo desde la última sync que trajo fechas, exitosa o parcial, y un banner si > 14 días.
 * Click → link a /admin/sync-vencimientos.
 */

import { Link } from 'react-router-dom'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { AlertTriangle, RefreshCw, Settings } from 'lucide-react'
import { useUltimaSync } from '@/core/hooks/useVencimientosSync'
import { usePermissions } from '@/hooks/usePermissions'

const STALE_DAYS = 14

function diasDesde(iso: string | null | undefined): number | null {
  if (!iso) return null
  const ms = Date.now() - new Date(iso).getTime()
  return Math.floor(ms / (24 * 60 * 60 * 1000))
}

function formatearDia(iso: string | null | undefined): string {
  if (!iso) return ''
  return new Date(iso).toLocaleDateString('es-AR', { day: 'numeric', month: 'long' })
}

export function UltimaSyncWidget() {
  const { puede } = usePermissions()
  const { data: ultima } = useUltimaSync('ARCA')

  if (!puede.verSyncVencimientos) return null

  const dias = diasDesde(ultima?.sync_finished_at ?? null)
  const stale = dias != null && dias > STALE_DAYS
  const sinData = ultima == null

  if (stale) {
    return (
      <Alert className="border-amber-300 bg-amber-50 text-amber-900 dark:border-amber-800 dark:bg-amber-950/30 dark:text-amber-100 [&>svg]:text-amber-600">
        <AlertTriangle className="h-4 w-4" />
        <AlertTitle>Calendario fiscal desactualizado</AlertTitle>
        <AlertDescription className="flex items-center justify-between">
          <span>
            Desde el {formatearDia(ultima?.sync_finished_at)} no se pudieron traer fechas nuevas de
            ARCA. Si ARCA movió algún vencimiento, todavía no se refleja en tu agenda. El aviso ya
            le llegó a soporte.
          </span>
          <Link
            to="/admin/sync-vencimientos"
            className="ml-3 inline-flex items-center gap-1 text-sm underline whitespace-nowrap"
          >
            <Settings className="h-3.5 w-3.5" />
            Ver admin
          </Link>
        </AlertDescription>
      </Alert>
    )
  }

  // No mostrar widget si nunca hubo sync exitosa todavía (sin ruido)
  if (sinData) return null

  return (
    <div className="flex items-center justify-end text-xs text-muted-foreground gap-2 -mt-2">
      <RefreshCw className="h-3.5 w-3.5" />
      <span>
        Última sync ARCA: hace {dias} {dias === 1 ? 'día' : 'días'}
        {ultima.status === 'partial' ? ', parcial' : ''}.
      </span>
      <Link to="/admin/sync-vencimientos" className="underline">
        Admin
      </Link>
    </div>
  )
}
